/**
 * Datadog RUM initialization and SDK integration
 */

import { datadogRum } from "@datadog/browser-rum";
import type { DatadogUser } from "./types";
import { loadDatadogConfig } from "./config";
import { maskPIIInObject } from "./pii-masking";
import { logger } from "./logger";

let isInitialized = false;

/**
 * Initialize Datadog RUM
 * Should be called as early as possible in the application lifecycle
 */
export function initializeDatadogRUM(): void {
  if (isInitialized) {
    logger.warn("RUM already initialized");
    return;
  }

  const config = loadDatadogConfig();

  if (!config) {
    // Configuration loading already logged the reason
    return;
  }

  try {
    datadogRum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: config.site,
      service: config.service,
      env: config.env,
      version: config.version,
      sessionSampleRate: config.sessionSampleRate,
      sessionReplaySampleRate: config.sessionReplaySampleRate,
      trackUserInteractions: config.trackUserInteractions,
      trackResources: config.trackResources,
      trackLongTasks: config.trackLongTasks,
      defaultPrivacyLevel: "mask-user-input",
      // PII masking hook - sanitizes all events before sending to Datadog
      beforeSend: (event) => {
        try {
          // Apply PII masking to the entire event
          maskPIIInObject(event);
          // Return true to allow the event to be sent
          return true;
        } catch (error) {
          logger.error("Error in beforeSend hook:", error);
          // Return true to allow the event to be sent even if masking fails
          return true;
        }
      },
    });

    datadogRum.startSessionReplayRecording();

    isInitialized = true;
    logger.info(`RUM initialized successfully (env: ${config.env}, service: ${config.service})`);
  } catch (error) {
    logger.error("Failed to initialize RUM:", error);
  }
}

/**
 * Set the current user context
 * Email addresses are preserved for user tracking
 */
export function setDatadogUser(user: DatadogUser | null): void {
  if (!isInitialized) {
    logger.warn("Cannot set user: RUM not initialized");
    return;
  }

  try {
    if (user === null) {
      datadogRum.clearUser();
    } else {
      datadogRum.setUser(user);
    }
  } catch (error) {
    logger.error("Failed to set user:", error);
  }
}

/**
 * Add a custom error to RUM
 */
export function addDatadogError(error: Error | unknown, context?: Record<string, unknown>): void {
  if (!isInitialized) {
    logger.warn("Cannot add error: RUM not initialized");
    return;
  }

  try {
    datadogRum.addError(error, context);
  } catch (err) {
    logger.error("Failed to add error:", err);
  }
}

/**
 * Add custom action tracking
 */
export function addDatadogAction(name: string, context?: Record<string, unknown>): void {
  if (!isInitialized) {
    logger.warn("Cannot add action: RUM not initialized");
    return;
  }

  try {
    datadogRum.addAction(name, context);
  } catch (error) {
    logger.error("Failed to add action:", error);
  }
}

/**
 * Add custom timing metric
 */
export function addDatadogTiming(name: string, duration?: number): void {
  if (!isInitialized) {
    logger.warn("Cannot add timing: RUM not initialized");
    return;
  }

  try {
    datadogRum.addTiming(name, duration);
  } catch (error) {
    logger.error("Failed to add timing:", error);
  }
}

/**
 * Check if Datadog RUM is initialized
 */
export function isDatadogInitialized(): boolean {
  return isInitialized;
}
